/** Labels and formatting for the pipeline results dashboard. Pure. */

export const RUN_STATUS_LABEL: Record<string, string> = {
  queued: "Queued",
  running: "Running",
  waiting: "Waiting for approval",
  cancelling: "Stopping",
  cancelled: "Stopped",
  succeeded: "Succeeded",
  failed: "Failed",
};

export const STATUS_FILTER_OPTIONS = [
  { value: "succeeded", label: "Succeeded" },
  { value: "failed", label: "Failed" },
  { value: "running", label: "Running" },
  { value: "queued", label: "Queued" },
  { value: "waiting", label: "Waiting for approval" },
  { value: "cancelled", label: "Stopped" },
];

export const OUTCOME_LABEL: Record<string, string> = {
  loaded: "Loaded",
  updated: "Updated",
  unchanged: "Already up to date",
  failed: "Failed",
  unknown: "Not confirmed",
  not_sent: "Not sent",
};

/** Outcome → Mantine colour, from the status set (green · red · yellow · gray). */
export const OUTCOME_COLOR: Record<string, string> = {
  loaded: "green",
  updated: "green",
  unchanged: "gray",
  failed: "red",
  unknown: "yellow",
  not_sent: "gray",
};

export const TRIGGER_LABEL: Record<string, string> = {
  manual: "Manual",
  schedule: "Schedule",
  blob: "File arrival",
  pipeline: "Another pipeline",
};

export const PERIOD_OPTIONS = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "365", label: "Last 12 months" },
];

/**
 * The status a business user should see for a run: the pipeline can finish
 * ("succeeded") while every document it tried to load into Veeva failed, so the
 * badge follows the documents, not just the run.
 */
export function runResult(run: { status: string; uploaded: number; failed: number }): {
  label: string;
  color: string;
} {
  if (run.status === "succeeded" && run.failed > 0) {
    return run.uploaded === 0
      ? { label: "Failed", color: "red" }
      : { label: "Partly failed", color: "orange" };
  }
  return { label: RUN_STATUS_LABEL[run.status] ?? run.status, color: "" };
}

export function runStatusLabel(status: string): string {
  return RUN_STATUS_LABEL[status] ?? status;
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 1) return "< 1 s";
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m < 60) return s ? `${m} min ${s} s` : `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h ${m % 60} min`;
}

export function formatPercent(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return "—";
  return `${Math.round(rate * 100)}%`;
}

export function formatCount(n: number): string {
  return n.toLocaleString("en-GB");
}
